import { logger } from '../../services/logger.ts';
import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowDownRight, 
  ArrowUpRight, 
  Wallet, 
  Hourglass,
  CheckCircle, 
  XCircle, 
  Filter,
  ChevronDown,
  Search,
  Download,
  FileText,
  File,
  Briefcase,
  Gift
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSettings } from '../../lib/settings/settingsContext.tsx';
import { WalletTransaction, UserBet } from '../../types.ts';
import { supabase } from '../../lib/db.ts';
import { BetTransactionService } from '../../services/BetTransactionService.ts';
import { MarketEngine } from '../../lib/marketEngine.ts';
import { toast } from 'react-hot-toast';
import { ConfirmationModal } from "../common/ConfirmationModal.tsx";
import { useAuth } from "../../lib/auth.tsx";

interface TransactionHistoryProps {
  transactions: WalletTransaction[];
  currentBalance?: number; // Add optional current balance prop
}

interface MarketInfo {
  id: string;
  title: string;
  status?: string;
  trading_end_time?: string;
}

interface BetDetailWithRelations extends UserBet {
  market?: { id: string; title: string };
  market_option?: { id: string; name: string };
}

export const TransactionHistory: React.FC<TransactionHistoryProps> = ({ transactions, currentBalance }) => {
  const { formatCurrency } = useSettings();
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [dateRange, setDateRange] = useState<'all' | 'today' | 'week' | 'month' | 'quarter' | 'year' | 'custom'>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [marketInfo, setMarketInfo] = useState<Record<string, MarketInfo>>({});
  const [betDetails, setBetDetails] = useState<Record<string, BetDetailWithRelations>>({});
  const [cancelingBetId, setCancelingBetId] = useState<string | null>(null);
  const marketEngine = new MarketEngine();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [txToConfirm, setTxToConfirm] = useState<WalletTransaction | null>(null);
  const [confirmDetails, setConfirmDetails] = useState<{ marketTitle?: string; optionName?: string; betAmount?: number; refundAmount?: number } | null>(null);
  const [fetchedBalance, setFetchedBalance] = useState<number | null>(null);
  const { user } = useAuth();

  const filtersRef = useRef<HTMLDivElement>(null);
  const exportRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target as Node)) {
        setShowFilters(false);
      }
      if (exportRef.current && !exportRef.current.contains(event.target as Node)) {
        setShowExportOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch wallet balance if not provided
  useEffect(() => {
    const fetchBalance = async () => {
      if (currentBalance !== undefined || !user) return;
      
      try {
        const { data, error } = await supabase
          .from('user_wallets')
          .select('balance')
          .eq('user_id', user.id)
          .single();
          
        if (error) {
          logger.error('Error fetching wallet balance:', error);
          return;
        }
        
        if (data) {
          setFetchedBalance(data.balance);
        }
      } catch (err) {
        logger.error('Failed to fetch wallet balance:', err);
      }
    };
    
    fetchBalance();
  }, [currentBalance, user]);

  // Use provided balance or fetched balance
  const displayBalance = currentBalance !== undefined ? currentBalance : fetchedBalance;

  // Fetch market information for related transactions
  useEffect(() => {
    const fetchMarketInfo = async () => {
      // Extract all unique market IDs from transaction descriptions
      const marketIds = new Set<string>();
      transactions.forEach(tx => {
        // Check if description contains a market ID (UUID format)
        const marketIdMatch = tx.description?.match(/market ([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
        if (marketIdMatch && marketIdMatch[1]) {
          marketIds.add(marketIdMatch[1]);
        }
      });

      if (marketIds.size === 0) return;

      // Fetch market info from database, including trading_end_time
      const { data, error } = await supabase
        .from('markets')
        .select('id, title, status, trading_end_time')
        .in('id', Array.from(marketIds));

      if (error) {
        logger.error('Error fetching market information:', error);
        return;
      }

      if (data) {
        // Create lookup object
        const marketLookup: Record<string, MarketInfo> = {};
        data.forEach(market => {
          marketLookup[market.id] = {
            id: market.id,
            title: market.title,
            status: market.status,
            trading_end_time: market.trading_end_time
          };
        });
        setMarketInfo(marketLookup);
      }
    };

    fetchMarketInfo();
  }, [transactions]);

  // Fetch full bet details for relevant transactions
  useEffect(() => {
    const fetchBetDetails = async () => {
      const betIds = new Set<string>();
      transactions.forEach(tx => {
        if ((tx.type === 'bet_placement' || tx.type === 'payout') && tx.related_entity_id && 
            (tx.related_entity_type === 'bet' || tx.related_entity_type === 'user_bet')) {
          betIds.add(tx.related_entity_id);
        }
      });

      if (betIds.size === 0) {
        setBetDetails({}); // Clear if no bets
        return;
      }

      const betIdArray = Array.from(betIds);
      const chunkSize = 50; // Supabase URL length limit is around 8KB, 50 UUIDs (36 chars each) + query params should be safe.
      const fetchedBetData: BetDetailWithRelations[] = [];

      for (let i = 0; i < betIdArray.length; i += chunkSize) {
        const chunk = betIdArray.slice(i, i + chunkSize);
        const { data: chunkData, error: chunkError } = await supabase
          .from('user_bets')
          .select(`
            *,
            market:markets!inner(id, title),
            market_option:market_options!inner(id, name)
          `) // Join with markets and market_options
          .in('id', chunk);

        if (chunkError) {
          logger.error('Error fetching bet details chunk:', chunkError);
          // Decide if you want to skip this chunk or stop the whole process
          continue; 
        }
        if (chunkData) {
          fetchedBetData.push(...chunkData as BetDetailWithRelations[]);
        }
      }

      if (fetchedBetData.length > 0) {
        const betLookup: Record<string, BetDetailWithRelations> = {};
        fetchedBetData.forEach(bet => {
          betLookup[bet.id] = bet;
        });
        setBetDetails(betLookup);
      } else {
        // This case handles if all chunks fail or return no data
        setBetDetails({}); 
      }
    };

    fetchBetDetails();
  }, [transactions]);

  // Cancel trade functionality disabled for users
  // Renamed original cancelBet, now it fetches details and opens modal
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const requestCancelBet = async (transaction: WalletTransaction) => {
    if (!transaction.related_entity_id || transaction.related_entity_type !== 'bet') {
      toast.error("Invalid transaction for cancellation.");
      return;
    }
    const betId = transaction.related_entity_id;

    // Extract market ID from description
    const marketIdMatch = transaction.description?.match(/market ([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    if (!marketIdMatch || !marketIdMatch[1]) {
      toast.error("Could not determine market ID for this trade.");
      return;
    }
    const marketId = marketIdMatch[1];

    setCancelingBetId(betId); // Show spinner early
    try {
      // Fetch details needed for confirmation message and final checks
      const { data: betData, error: betError } = await supabase
        .from('user_bets')
        .select('id, status, amount, amount_after_fee, market_option_id')
        .eq('id', betId)
        .single();
        
      if (betError || !betData) throw new Error(betError?.message || 'Failed to fetch bet details.');
      
      const market = await marketEngine.getMarket(marketId);
      if (!market) throw new Error('Failed to fetch market details.');

      // --- Perform Pre-Checks (same as before, but inside request) --- 
      const now = new Date();
      let canCancel = true;
      let cancelReason = "";
      if (betData.status !== 'active') {
        canCancel = false;
        cancelReason = `Trade is not active (status: ${betData.status}).`;
      } else if (market.status !== 'active') {
         canCancel = false;
         cancelReason = `Market is not active (status: ${market.status}).`;
      } else if (market.resolution_time && new Date(market.resolution_time) <= now) {
         canCancel = false;
         cancelReason = "Market has reached its resolution time.";
      } else {
         const option = market.market_options.find(o => o.id === betData.market_option_id);
         if (!option) {
             canCancel = false;
             cancelReason = "Could not find corresponding market option.";
         } else {
             if (market.market_type === 'fixed') {
                 if (market.trading_end_time && new Date(market.trading_end_time) <= now) {
                     canCancel = false;
                     cancelReason = "Trading has ended for this fixed market.";
                 }
             } else if (market.market_type === 'knockout') {
                 if (option.trading_end_time && new Date(option.trading_end_time) <= now) {
                     canCancel = false;
                     cancelReason = "Trading has ended for this specific knockout option.";
                 } else if (market.trading_end_time && new Date(market.trading_end_time) <= now) {
                     canCancel = false;
                     cancelReason = "Overall trading has ended for this knockout market.";
                 }
             }
         }
      }

      if (!canCancel) {
        toast.error(`Cannot cancel trade: ${cancelReason}`);
        setCancelingBetId(null);
        return;
      }
      // --- End Pre-Checks ---
      
      // If checks pass, set state for modal
      setTxToConfirm(transaction);
      setConfirmDetails({
        marketTitle: market.title,
        optionName: market.market_options.find(o => o.id === betData.market_option_id)?.name,
        betAmount: betData.amount,
        refundAmount: betData.amount_after_fee,
      });
      setIsConfirmOpen(true);

    } catch (error) {
        logger.error('Error preparing bet cancellation:', error);
        toast.error(error instanceof Error ? error.message : 'Could not get trade details for cancellation.');
    } finally {
        // Clear loading state if modal wasn't opened
        if (!isConfirmOpen) setCancelingBetId(null);
    }
  };

  // Performs the actual cancellation
  const handleConfirmCancel = async () => {
    if (!txToConfirm || !txToConfirm.related_entity_id) return;

    const betId = txToConfirm.related_entity_id;
    // Keep loading state active
    // setCancelingBetId(betId); 
    setIsConfirmOpen(false);

    try {
      const result = await BetTransactionService.cancelBet(betId);
      if (result) {
        // Update the bet details state
        setBetDetails(prev => ({
          ...prev,
          [betId]: { ...(prev[betId] || {}), status: 'cancelled' } as BetDetailWithRelations 
        }));
        toast.success(`Trade cancelled successfully. ${confirmDetails?.refundAmount ? formatCurrency(confirmDetails.refundAmount) : 'Your refund'} will be added.`);
        window.dispatchEvent(new CustomEvent('betHistoryUpdated'));
        window.dispatchEvent(new CustomEvent('walletBalanceUpdated'));
      }
    } catch (error) {
      logger.error('Error cancelling trade:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to cancel trade');
      // Optionally refetch status on error
    } finally {
      setCancelingBetId(null);
      setTxToConfirm(null);
      setConfirmDetails(null);
    }
  };

  // Filter transactions
  const filteredTransactions = transactions.filter(transaction => {
    // Type filter
    if (typeFilter) {
      // Special case for winning bets filter
      if (typeFilter === 'winning_bets') {
        // Include by transaction type
        if (transaction.type === 'bet_win' || transaction.type === 'payout') {
          return true;
        }
        // Also include by related entity type
        if (transaction.related_entity_type === 'bet_payout') {
          return true;
        }
        return false;
      } else if (transaction.type !== typeFilter) {
        return false;
      }
    }
    
    // Status filter
    if (statusFilter && transaction.status !== statusFilter) return false;
    
    // Search by description
    if (searchQuery && transaction.description && 
        !transaction.description.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Date range filter
    if (dateRange !== 'all') {
      const txDate = new Date(transaction.created_at);
      const now = new Date();
      
      if (dateRange === 'today') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (txDate < today) {
          return false;
        }
      } else if (dateRange === 'week' && (now.getTime() - txDate.getTime() > 7 * 24 * 60 * 60 * 1000)) {
        return false;
      } else if (dateRange === 'month' && (now.getTime() - txDate.getTime() > 30 * 24 * 60 * 60 * 1000)) {
        return false;
      } else if (dateRange === 'quarter' && (now.getTime() - txDate.getTime() > 90 * 24 * 60 * 60 * 1000)) {
        return false;
      } else if (dateRange === 'year' && (now.getTime() - txDate.getTime() > 365 * 24 * 60 * 60 * 1000)) {
        return false;
      } else if (dateRange === 'custom') {
        // For custom date range
        if (startDate && new Date(startDate) > txDate) {
          return false;
        }
        if (endDate) {
          const endDateTime = new Date(endDate);
          endDateTime.setHours(23, 59, 59, 999); // End of the selected day
          if (endDateTime < txDate) {
            return false;
          }
        }
      }
    }
    
    return true;
  });

  // Format transaction description to replace market IDs with names and links
  const formatDescription = (transaction: WalletTransaction) => {
    if (!transaction.description) return '';
    
    // Special handling for payouts
    if (transaction.type === 'payout' && transaction.related_entity_id && 
        transaction.related_entity_type === 'user_bet') {
      const betId = transaction.related_entity_id;
      const betDetail = betDetails[betId];
      
      if (betDetail && betDetail.market && betDetail.market_option) {
        return (
          <>
            Payout for trade on <strong>{betDetail.market_option.name}</strong> in market{' '}
            <Link to={`/markets/${betDetail.market.id}`} className="text-wayja-teal hover:underline">
              {betDetail.market.title}
            </Link>
          </>
        );
      }
    }
    
    // Check if description contains a market ID (UUID format)
    // Handle both formats:
    // - "Trade placed on X in market UUID"
    // - "Trade placed on X in market Title (market UUID)"
    const marketIdMatch = transaction.description.match(/market ([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    
    if (marketIdMatch && marketIdMatch[1]) {
      const marketId = marketIdMatch[1];
      const market = marketInfo[marketId];
      
      // Check if the description already includes the market title
      const titlePattern = /in market (.*?) \(market [0-9a-f-]+\)/i;
      const titleMatch = transaction.description.match(titlePattern);
      
      if (titleMatch) {
        // New format: Description already has the market title, just make it a link
        const beforeMarketTitle = transaction.description.split(`in market ${titleMatch[1]}`)[0];
        const afterMarketId = transaction.description.split(`(market ${marketId})`)[1] || '';
        
        return (
          <>
            {beforeMarketTitle}in market <Link to={`/markets/${marketId}`} className="text-wayja-teal hover:underline">{titleMatch[1]}</Link>{afterMarketId}
          </>
        );
      } else if (market) {
        // Old format: Replace the market ID with a link to the market title
        const beforeMarket = transaction.description.split(`market ${marketId}`)[0];
        const afterMarket = transaction.description.split(`market ${marketId}`)[1] || '';
        
        return (
          <>
            {beforeMarket}market <Link to={`/markets/${marketId}`} className="text-wayja-teal hover:underline">{market.title}</Link>{afterMarket}
          </>
        );
      }
    }
    
    return transaction.description;
  };

  // Calculate transaction totals
  const totalDeposits = filteredTransactions
    .filter(tx => tx.type === 'deposit')
    .reduce((sum, tx) => sum + tx.amount, 0);
    
  const totalWithdrawals = filteredTransactions
    .filter(tx => tx.type === 'withdrawal')
    .reduce((sum, tx) => sum + tx.amount, 0);

  const totalBets = filteredTransactions
    .filter(tx => tx.type === 'bet_placement' || tx.type === 'service_fee' )
    .reduce((sum, tx) => sum + tx.amount, 0);
    
  const totalWinnings = filteredTransactions
    .filter(tx => {
      // Include by transaction type
      if ((tx.type === 'bet_win' || tx.type === 'payout')) {
        return true;
      }
      // Also include by related entity type for completeness
      if (tx.related_entity_type === 'bet_payout') {
        return true;
      }
      return false;
    })
    .reduce((sum, tx) => sum + tx.amount, 0);

  // Calculate net balance from all transactions
  // const netBalance = filteredTransactions
  //   .filter(tx => tx.status === 'completed')
  //   .reduce((sum, tx) => {
  //     if (['deposit', 'bet_win', 'bet_cancelled', 'payout', 'fee_refund'].includes(tx.type)) {
  //       return sum + tx.amount;
  //     } else if (['withdrawal', 'bet_placement', 'service_fee'].includes(tx.type)) {
  //       return sum + tx.amount;
  //     }
  //     return sum;
  //   }, 0);

  // Function to format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Function to get a user-friendly display type for a transaction
  const getTransactionDisplayType = (transaction: WalletTransaction) => {
    // First check related_entity_type if present
    if (transaction.related_entity_type) {
      switch (transaction.related_entity_type) {
        case 'bet_payout':
          return 'Trade Payout';
        case 'affiliate':
          return 'Affiliate Commission';
        case 'user_bet':
          if (transaction.type === 'payout') {
            return 'Market Win';
          }
          // Fall through to other cases
        case 'bet':
          // For bet entity, check transaction type
          if (transaction.type === 'bet_win') {
            return 'Trade Win';
          }
          if (transaction.type === 'bet_loss') {
            return 'Trade Loss';
          }
          if (transaction.type === 'bet_cancelled') {
            return 'Trade Refund';
          }
          if (transaction.type === 'market_cancellation_refund') {
            return 'Market cancellation refund';
          }
          if (transaction.type === 'service_fee') {
            return 'Service Fee';
          }
          if (transaction.type === 'fee_refund') {
            return 'Fee Refund';
          }
          if (transaction.type === 'affiliate_commission') {
            return 'Affiliate Commission';
          }
          if (transaction.type === 'creator_commission') {
            return 'Creator Commission';
          }
          
          // Only show as Trade Loss if explicitly marked as bet_loss
          // Don't mark active trades as losses just because they're completed transactions
          if (transaction.type === 'bet_placement') {
            return 'Trade Placement';
          }
          
          return 'Trade Placement';
      }
    }
    
    // If no related_entity_type, use transaction type
    switch (transaction.type) {
      case 'deposit':
        return 'Deposit';
      case 'withdrawal':
        return 'Withdrawal';
      case 'bet_placement':
        return 'Trade Placement';
      case 'bet_win':
        return 'Trade Win';
      case 'bet_loss':
        return 'Trade Loss';
      case 'bet_cancelled':
        return 'Trade Refund';
      case 'market_cancellation_refund':
        return 'Market cancellation refund';
      case 'service_fee':
        return 'Service Fee';
      case 'payout':
        return 'Payout';
      case 'fee_refund':
        return 'Fee Refund';
      case 'affiliate_commission':
        return 'Affiliate Commission';
      case 'creator_commission':
        return 'Creator Commission';
      default:
        return 'Transaction';
    }
  };

  // Function to get transaction icon
  const getTransactionIcon = (transaction: WalletTransaction) => {
    // Determine type based on related entity first
    if (transaction.related_entity_type) {
      switch (transaction.related_entity_type) {
        case 'user_bet':
          if (transaction.type === 'payout') {
            return <ArrowDownRight className="w-4 h-4 shrink-0" />;
          }
          // Fall through to other cases
        case 'bet':
          // Check transaction type for bet-related transactions
          if (transaction.type === 'bet_win') {
            return <ArrowDownRight className="w-4 h-4 shrink-0" />;
          } else if (transaction.type === 'bet_loss') {
            return <ArrowUpRight className="w-4 h-4 shrink-0" />;
          } else if (transaction.type === 'bet_cancelled') {
            return <ArrowDownRight className="w-4 h-4 shrink-0" />;
          } else if (transaction.type === 'market_cancellation_refund') {
            return <ArrowDownRight className="w-4 h-4 shrink-0" />;
          } else if (transaction.type === 'service_fee') {
            return <ArrowUpRight className="w-4 h-4 shrink-0" />;
          } else if (transaction.type === 'fee_refund') {
            return <ArrowDownRight className="w-4 h-4 shrink-0" />;
          } else {
            // Always use bet placement icon for placement transactions
            return <ArrowUpRight className="w-4 h-4 shrink-0" />;
          }
        case 'bet_payout':
          return <ArrowDownRight className="w-4 h-4 shrink-0" />;
        case 'deposit':
          return <ArrowDownRight className="w-4 h-4 shrink-0" />;
        case 'withdrawal':
          return <ArrowUpRight className="w-4 h-4 shrink-0" />;
        case 'affiliate':
           return <Gift className="w-4 h-4 shrink-0" />;
      }
    }
    
    // Fall back to transaction type
    switch (transaction.type) {
      case 'deposit':
        return <ArrowDownRight className="w-4 h-4 shrink-0" />;
      case 'withdrawal':
        return <ArrowUpRight className="w-4 h-4 shrink-0" />;
      case 'bet_placement':
        // Always use bet placement icon
        return <ArrowUpRight className="w-4 h-4 shrink-0" />;
      case 'bet_win':
        return <ArrowDownRight className="w-4 h-4 shrink-0" />;
      case 'bet_loss':
        return <ArrowUpRight className="w-4 h-4 shrink-0" />;
      case 'bet_cancelled':
        return <ArrowDownRight className="w-4 h-4 shrink-0" />;
      case 'market_cancellation_refund':
        return <ArrowDownRight className="w-4 h-4 shrink-0" />;
      case 'service_fee':
        return <ArrowUpRight className="w-4 h-4 shrink-0" />;
      case 'payout':
        return <ArrowDownRight className="w-4 h-4 shrink-0" />;
      case 'fee_refund':
        return <ArrowDownRight className="w-4 h-4 shrink-0" />;
      case 'affiliate_commission':
        return <Gift className="w-4 h-4 shrink-0" />;
      case 'creator_commission':
        return <Briefcase className="w-4 h-4 shrink-0" />;
      default:
        return <Wallet className="w-4 h-4 shrink-0" />;
    }
  };

  // Function to get status icon
  const getStatusIcon = (status: string) => {
    if (status === 'completed') {
      return <CheckCircle className="w-4 h-4 text-green-600" aria-hidden />;
    } else if (status === 'failed') {
      return <XCircle className="w-4 h-4 text-red-600" aria-hidden />;
    } else {
      return <Hourglass className="w-4 h-4 text-yellow-600" aria-hidden />;
    }
  };

  // Function to determine if the transaction should show a positive sign
  const isPositiveTransaction = (transaction: WalletTransaction) => {
    // Transactions that add money to the wallet should be positive
    return ['deposit', 'bet_win', 'bet_cancelled', 'market_cancellation_refund', 'payout', 'fee_refund', 'withdrawal_refund', 'affiliate_commission', 'creator_commission'].includes(transaction.type) || 
           transaction.related_entity_type === 'bet_payout' || 
           transaction.related_entity_type === 'deposit' ||
           transaction.related_entity_type === 'affiliate';
  };

  const getTransactionTypeTextClass = (transaction: WalletTransaction) => {
    if (getTransactionDisplayType(transaction) === 'Transaction') {
      return 'text-gray-700';
    }
    return isPositiveTransaction(transaction) ? 'text-green-500' : 'text-orange-500';
  };

  // Function to format the transaction amount
  const formatTransactionAmount = (transaction: WalletTransaction) => {
    const isPositive = isPositiveTransaction(transaction);
    const absAmount = Math.abs(transaction.amount);
    return `${isPositive ? '+' : '-'}${formatCurrency(absAmount)}`;
  };

  // Function to export transactions to CSV
  const exportToCSV = () => {
    // Define CSV header
    const header = [
      'Type',
      'Description',
      'Amount',
      'Date',
      'Status'
    ].join(',');

    // Convert each transaction to CSV row
    const csvRows = filteredTransactions.map(transaction => {
      const type = getTransactionDisplayType(transaction);
      // Clean description by removing any HTML/JSX elements
      const description = transaction.description?.replace(/<[^>]*>?/gm, '') || '';
      const amount = isPositiveTransaction(transaction) 
        ? transaction.amount 
        : -Math.abs(transaction.amount);
      const date = new Date(transaction.created_at).toISOString();
      const status = transaction.status;
      
      // Escape any commas in fields
      return [
        `"${type}"`,
        `"${description.replace(/"/g, '""')}"`,
        amount,
        `"${date}"`,
        `"${status}"`
      ].join(',');
    });

    // Combine header with rows
    const csvContent = [header, ...csvRows].join('\n');
    
    // Create a blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `transaction_history_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to export transactions to PDF (using browser's print functionality)
  const exportToPDF = () => {
    // Create a printable version of the transactions
    const title = `Transaction History (${new Date().toLocaleDateString()})`;
    const printContent = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { text-align: center; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .amount-positive { color: green; }
            .amount-negative { color: red; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${filteredTransactions.map(tx => `
                <tr>
                  <td>${getTransactionDisplayType(tx)}</td>
                  <td>${tx.description?.replace(/<[^>]*>?/gm, '') || ''}</td>
                  <td class="${isPositiveTransaction(tx) ? 'amount-positive' : 'amount-negative'}">
                    ${isPositiveTransaction(tx) ? '+' : '-'}${formatCurrency(Math.abs(tx.amount))}
                  </td>
                  <td>${formatDate(tx.created_at)}</td>
                  <td>${tx.status}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="footer">
            Generated from Wayja on ${new Date().toLocaleString()}
            ${user ? `for ${user.email}` : ''}
          </div>
        </body>
      </html>
    `;
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      // Give the browser a moment to load the content before printing
      setTimeout(() => {
        printWindow.print();
        // Close the window after print dialog closes (optional)
        printWindow.onafterprint = () => printWindow.close();
      }, 500);
    } else {
      toast.error('Please allow popups for this website to export to PDF');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h3 className="text-lg font-semibold">Transaction History</h3>
        
        <div className="flex flex-col sm:flex-row gap-3 relative">
          {/* Search */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search transactions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm leading-5"
            />
          </div>
          
          {/* Filter dropdown */}
          <div className="relative" ref={filtersRef}>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 py-2 border border-gray-300 rounded-md flex items-center gap-2 w-full sm:w-auto justify-center"
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
            
            {showFilters && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-200">
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="font-medium text-sm">Transaction Type</p>
                </div>
                <div className="mt-2 space-y-1 px-3">
                  <button
                    onClick={() => {
                      setTypeFilter(null);
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${typeFilter === null ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    All Types
                  </button>
                  <button
                    onClick={() => {
                      setTypeFilter('deposit');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${typeFilter === 'deposit' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    Deposits
                  </button>
                  <button
                    onClick={() => {
                      setTypeFilter('withdrawal');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${typeFilter === 'withdrawal' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    Withdrawals
                  </button>
                  <button
                    onClick={() => {
                      setTypeFilter('bet_placement');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${typeFilter === 'bet_placement' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    Trade Placements
                  </button>
                  <button
                    onClick={() => {
                      setTypeFilter('winning_bets');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${typeFilter === 'winning_bets' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    Winning Trades
                  </button>
                  <button
                    onClick={() => {
                      setTypeFilter('bet_loss');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${typeFilter === 'bet_loss' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    Lost Trades
                  </button>
                  <button
                    onClick={() => {
                      setTypeFilter('bet_cancelled');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${typeFilter === 'bet_cancelled' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    Cancelled Trades
                  </button>
                  <button
                    onClick={() => {
                      setTypeFilter('service_fee');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${typeFilter === 'service_fee' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    Service Fees
                  </button>
                  <button
                    onClick={() => {
                      setTypeFilter('affiliate_commission');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${typeFilter === 'affiliate_commission' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    Affiliate Commission
                  </button>
                  <button
                    onClick={() => {
                      setTypeFilter('creator_commission');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${typeFilter === 'creator_commission' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    Creator Commission
                  </button>
                </div>
                
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="font-medium text-sm">Status</p>
                </div>
                <div className="mt-2 space-y-1 px-3">
                  <button
                    onClick={() => {
                      setStatusFilter(null);
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${statusFilter === null ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    All Statuses
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter('completed');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${statusFilter === 'completed' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    Completed
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter('pending');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${statusFilter === 'pending' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    Pending
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter('failed');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${statusFilter === 'failed' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    Failed
                  </button>
                </div>
                
                <div className="px-3 py-2">
                  <p className="font-medium text-sm">Date Range</p>
                </div>
                <div className="mt-2 space-y-1 px-3">
                  <button
                    onClick={() => {
                      setDateRange('all');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${dateRange === 'all' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    All Time
                  </button>
                  <button
                    onClick={() => {
                      setDateRange('today');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${dateRange === 'today' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    Today
                  </button>
                  <button
                    onClick={() => {
                      setDateRange('week');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${dateRange === 'week' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    Last 7 Days
                  </button>
                  <button
                    onClick={() => {
                      setDateRange('month');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${dateRange === 'month' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    Last 30 Days
                  </button>
                  <button
                    onClick={() => {
                      setDateRange('quarter');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${dateRange === 'quarter' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    Last 90 Days
                  </button>
                  <button
                    onClick={() => {
                      setDateRange('year');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${dateRange === 'year' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    Last 365 Days
                  </button>
                  <button
                    onClick={() => {
                      setDateRange('custom');
                      // Keep filter open for custom date selection
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${dateRange === 'custom' ? 'bg-gray-100 font-medium' : ''}`}
                  >
                    Custom Range
                  </button>
                  
                  {/* Custom date range fields */}
                  {dateRange === 'custom' && (
                    <div className="pt-2">
                      <div className="mb-2">
                        <label className="block text-xs text-gray-500 mb-1">From:</label>
                        <input 
                          type="date" 
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="w-full text-sm rounded border border-gray-300 px-2 py-1"
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-xs text-gray-500 mb-1">To:</label>
                        <input 
                          type="date" 
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="w-full text-sm rounded border border-gray-300 px-2 py-1"
                        />
                      </div>
                      <button
                        onClick={() => setShowFilters(false)}
                        className="w-full bg-wayja-teal text-white rounded px-2 py-1 text-sm mt-1"
                      >
                        Apply Date Range
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="px-3 py-2 border-t border-gray-100">
                  <button
                    onClick={() => {
                      setTypeFilter(null);
                      setStatusFilter(null);
                      setDateRange('all');
                      setShowFilters(false);
                    }}
                    className="w-full text-center text-wayja-teal text-sm font-medium"
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Export button */}
          <div className="relative" ref={exportRef}>
            <button
              onClick={() => setShowExportOptions(!showExportOptions)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm leading-5 bg-white"
            >
              <Download className="h-4 w-4 mr-1 text-gray-400" />
              <span>Export</span>
              <ChevronDown className={`ml-1 h-4 w-4 text-gray-400 transition-transform ${showExportOptions ? 'rotate-180' : ''}`} />
            </button>
            
            {showExportOptions && (
              <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white z-20 border border-gray-200">
                <div className="py-1">
                  <button
                    onClick={() => {
                      exportToCSV();
                      setShowExportOptions(false);
                    }}
                    className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FileText className="h-4 w-4 mr-2 text-green-600" />
                    Export to CSV
                  </button>
                  <button
                    onClick={() => {
                      exportToPDF();
                      setShowExportOptions(false);
                    }}
                    className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <File className="h-4 w-4 mr-2 text-red-600" />
                    Export to PDF
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Stats summary - make this more responsive for mobile */}
      <div className="px-4 py-3 bg-gray-50 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-4">
        <div className="lg:col-span-2 bg-white rounded p-3 shadow-sm flex flex-col justify-between">
          <p className="text-sm text-gray-500">Current Balance</p>
          <p className="text-xl font-bold text-wayja-teal">{displayBalance !== null && displayBalance !== undefined ? formatCurrency(displayBalance) : "—"}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Transactions</p>
          <p className="font-semibold">{filteredTransactions.length}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Deposits</p>
          <p className="font-semibold text-green-600">{formatCurrency(totalDeposits)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Withdrawals</p>
          <p className="font-semibold text-red-600">{formatCurrency(totalWithdrawals)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Trades</p>
          <p className="font-semibold text-orange-600">{formatCurrency(totalBets)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Winnings</p>
          <p className="font-semibold text-green-600">{formatCurrency(totalWinnings)}</p>
        </div>
        {/* <div>
          <p className="text-sm text-gray-500">Net Balance</p>
          <p className={`font-semibold ${netBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(netBalance)}
          </p>
        </div> */}
      </div>
      
      {/* Transaction list */}
      <div className="divide-y divide-gray-200">
        {filteredTransactions.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <div className="w-12 h-12 mx-auto mb-2 bg-gray-100 rounded-full flex items-center justify-center">
              <Wallet className="w-6 h-6 text-gray-400" />
            </div>
            <p>No transactions found</p>
            {(typeFilter || statusFilter || searchQuery || dateRange !== 'all') && (
              <button
                onClick={() => {
                  setTypeFilter(null);
                  setStatusFilter(null);
                  setDateRange('all');
                  setSearchQuery('');
                }}
                className="mt-2 text-sm text-wayja-teal"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th scope="col" className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th scope="col" className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th scope="col" className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-3 md:px-6 py-4 align-top whitespace-normal md:whitespace-nowrap max-w-[9rem] sm:max-w-none">
                      <div className={`flex flex-wrap items-center gap-1.5 text-xs font-medium max-w-full ${getTransactionTypeTextClass(transaction)}`}>
                        {getTransactionIcon(transaction)}
                        <span className="break-words">{getTransactionDisplayType(transaction)}</span>
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap align-top">
                      <div className={`text-sm font-medium ${isPositiveTransaction(transaction) ? 'text-green-600' : 'text-red-600'}`}>
                        {formatTransactionAmount(transaction)}
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-4 align-top">
                      <div className="text-sm text-gray-900">
                        {formatDescription(transaction)}
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap align-top">
                      <div className="text-sm text-gray-500">{formatDate(transaction.created_at)}</div>
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap align-top">
                      <div
                        className="flex items-center justify-start gap-1.5 text-sm"
                        aria-label={`Status: ${transaction.status}`}
                      >
                        {getStatusIcon(transaction.status)}
                        <span className="hidden md:inline capitalize">{transaction.status}</span>
                        <span className="sr-only md:hidden">{transaction.status}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Render the Confirmation Modal */}
      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => {
           setIsConfirmOpen(false);
           setCancelingBetId(null); // Clear loading if cancelled via modal close
           setTxToConfirm(null);
           setConfirmDetails(null);
        }}
        onConfirm={handleConfirmCancel}
        title="Confirm Trade Cancellation"
        message={
          confirmDetails ? (
            <div className="space-y-1 text-sm">
              <p>Are you sure you want to cancel this trade?</p>
              <p><strong>Market:</strong> {confirmDetails.marketTitle || 'N/A'}</p>
              <p><strong>Outcome:</strong> {confirmDetails.optionName || 'N/A'}</p>
              <p><strong>Trade Amount:</strong> {confirmDetails.betAmount ? formatCurrency(confirmDetails.betAmount) : 'N/A'}</p>
              <p><strong>Refund Amount (Net):</strong> {confirmDetails.refundAmount ? formatCurrency(confirmDetails.refundAmount) : 'N/A'}</p>
            </div>
          ) : (
            'Loading trade details...'
          )
        }
        confirmText="Cancel Trade"
        cancelText="Keep Trade"
        isLoading={!!cancelingBetId} // Show loading state on confirm button
        variant="danger"
      />
    </div>
  );
};